export interface feePaymentDetailDTOType {
  cashFeeAmount?: number;
  wxFeeAmount?: number;
  alipayFeeAmount?: number;
  transferFeeAmount?: number;
}

export interface paymentRecordRequestType<T> {
  list?: T;
  pagination?: {
    currentPage: number;
    pageSize: number;
    total: number;
  };
}

export interface paymentRecordType {
  billPeriod?: string;
  accountBalance?: number;
  amountDefault?: number;
  amountPreferential?: number;
  auditingTime?: string | null;
  auditingUserId?: string | null;
  communityId?: string;
  communityName?: string;
  creatorTime?: string;
  creatorUserId?: string;
  description?: string | null;
  feePaymentDetailDTO?: feePaymentDetailDTOType;
  feeTotal?: number;
  id?: string;
  invoiceState?: string | null;
  orgId?: string | null;
  orgName?: string | null;
  ownerId?: string;
  ownerName?: string;
  ownerNum?: string;
  paidAmount?: number;
  payableAmount?: number | null;
  paymentTime?: string;
  phoneNumber?: string;
  primeRate?: string;
  receivableAmount?: number;
  refundCheckState?: number;
  refundId?: string | null;
  refundRemarks?: string | null;
  refundState?: string;
  refundsApplyTime?: string | null;
  refundsMode?: string | null;
  refundsReason?: string | null;
  refundsTime?: string | null;
  refundsUserId?: string | null;
  roomNum?: string;
  expand?: boolean;
}

export interface paymentDetailType {
  additionalAmount?: number | null;
  amount?: number;
  amountDefault?: number;
  amountPreferential?: number;
  answerTime?: string;
  category?: number;
  chargeState?: number;
  communityId?: string;
  communityName?: string;
  consumption?: number;
  creatorTime?: string;
  creatorUserId?: string | null;
  deleteTime?: string | null;
  deleteUserId?: string | null;
  description?: string | null;
  feeConfigId?: string;
  feeEndDay?: string | null;
  feeName?: string;
  feeStartDay?: string | null;
  feeType?: string;
  generationMethod?: number;
  id?: string;
  interimFlag?: number;
  invoiceState?: string | null;
  meterStatusVOList?: any[] | null; // 根据实际情况定义具体类型
  orgId?: string;
  orgName?: string;
  ownerId?: string;
  ownerName?: string;
  ownerNum?: string;
  paidAmount?: number;
  payFeeType?: string | null;
  paymentBillId?: string;
  phoneNumber?: string;
  refundsReason?: string | null;
  roomBuiltUpArea?: number;
  roomId?: string;
  roomNum?: string;
  roomState?: string;
  roomType?: string;
  squarePrice?: number;
  state?: number;
  surplus?: number;
  totalAmount?: number;
  waterCardNumber?: string;
  waterLastCardInsertion?: string;
  yearsMonth?: string;
}

export interface paymentBillParamsType {
  communityId?: string;
  roomCode?: string;
  customerId?: string;
  checkoutStatus: checkoutStatusEnum;
  auditStatus: auditStatusEnum;
  billType: billTypeEnum;
}

/**
 * 结算状态枚举
 */
export enum checkoutStatusEnum {
  /** 已结算 */
  SETTLEMENT = 'SETTLEMENT',
  /** 未结算 */
  WAIT_SETTLEMENT = 'WAIT_SETTLEMENT',
}

/**
 * 审核状态枚举
 */
export enum auditStatusEnum {
  /** 审核中 */
  PENDING = 'PENDING',
  /** 已通过 */
  APPROVED = 'APPROVED',
  /** 已拒绝 */
  REJECTED = 'REJECTED',
  /** 作废 */
  CANCELLATION = 'CANCELLATION',
}

/**
 * 账单类型枚举
 */
export enum billTypeEnum {
  /** 常规 */
  GENERAL = 'GENERAL',
  /** 仪表 */
  METER = 'METER',
}

export const billTypeMap: Record<string, string> = {
  [billTypeEnum.GENERAL]: '常规',
  [billTypeEnum.METER]: '仪表',
};

export interface paymentBillType {
  billType: billTypeEnum;
  paymentBillRecordList: paymentBillRecordType[];
  expand: boolean;
  id: number;
}

/**
 * 历史账单字段详情
 */
export interface paymentBillRecordType {
  /** 序号 */
  sequenceNbr?: string;
  /** 创建人 */
  createBy?: string;
  /** 创建时间 */
  createTime?: string;
  /** 缴费单号 */
  paymentNo?: string;
  /** 账单编号 */
  billNo?: string;
  /** 账单类型 */
  billType?: string;
  /** 账单月份 */
  billMonth?: string;
  /** 费用项ID */
  feeItemId?: string;
  /** 费用项名称 */
  feeItemName?: string;
  /** 单价或金额 */
  priceOrAmount?: string;
  /** 使用量或面积 */
  usageOrAcreage?: string;
  /** 总金额 */
  totalAmount?: string;
  /** 应付金额 */
  payableAmount?: string;
  /** 优惠金额 */
  discountAmount?: string;
  /** 滞纳金 */
  penaltyAmount?: string;
  /** 余额抵扣 */
  balanceDeduction?: string;
  /** 实际支付金额 */
  actualAmount?: string;
  /** 是否临时费用 */
  isTemporary?: boolean;
  /** 是否退款 */
  refund?: boolean;
  /** 退款金额 */
  refundAmount?: string;
  /** 房间ID */
  roomId?: string;
  /** 房间编码 */
  roomCode?: string;
  /** 客户ID */
  customerId?: string;
  /** 单元ID */
  unitId?: string;
  /** 楼层ID */
  floorId?: string;
  /** 社区ID */
  communityId?: string;
  /** 区域ID */
  regionId?: string;
  /** 结算状态 */
  checkoutStatus?: string;
  /** 审核状态 */
  auditStatus?: string;
  /** 结算用户ID */
  checkoutUserId?: string;
  /** 结算时间 */
  checkoutTime?: string;
  /** 账单开始日期 */
  billStartDate?: string;
  /** 账单结束日期 */
  billEndDate?: string;
  /** 表计类型 */
  meterType?: string;
  /** 表计状态 */
  meterStatus?: string;
  /** 表计ID */
  meterId?: string;
  /** 卡号 */
  cardNo?: string | null;
  /** 刷卡时间 */
  swipingCardTime?: string;
  /** 剩余量 */
  surplus?: string;
  /** 上期读数 */
  priorPeriodReading?: string;
  /** 本期读数 */
  currentPeriodReading?: string;
  /** 附加量 */
  additional?: string;
  /** 优惠方式 */
  discountMethod?: string;
  /** 优惠比例 */
  discountPercent?: string;
  /** 项目参数值 */
  itemParameterValue?: string;
  /** 项目分类ID */
  itemCategoryId?: string;
  /** 项目分类名称 */
  itemCategoryName?: string;
  /** 房间全名 */
  roomName?: string;
  /** 建筑面积 */
  buildArea?: string;
  /** 客户名称 */
  customerName?: string;
  /** 手机号 */
  phone?: string | null;
  /** 费用绑定ID */
  feeBindId?: string;
  /** 最后审核人 */
  lastAuditUser?: string | null;
  /** 最后审核人ID */
  lastAuditUserId?: string | null;
  /** 最后审核时间 */
  lastAuditTime?: string | null;
  /** 创建方式 */
  creationMethod?: string;
  /** 系数 */
  coefficient?: string;
  /** 附加费 */
  additionalFee?: string;
  /** 金额计算规则 */
  amountCalRule?: string;
  /** 上期时间 */
  priorPeriodTime?: string | null;
  /** 本期时间 */
  currentPeriodTime?: string | null;
  /** 账单有效性 */
  billValid?: string;
  /** 结算用户 */
  checkoutUser?: string;
  /** 公司ID */
  companyId?: string;
  /** 图片 */
  images?: any | null;
  /** 附件 */
  attachment?: any | null;
  /** 优惠备注 */
  discountRemark?: string | null;
  /** 动态费用实例 */
  dynamicFeeInstance?: any[];
  /** 支付渠道 */
  payChannel?: string;
  /** 房间IDs */
  roomIds?: string | null;
  /** 账单编号列表 */
  billNos?: string | null;
  /** 上期用量 */
  preUsage?: string | null;
  /** 账单月份列表 */
  billMonthList?: string | null;
  /** 当前读数 */
  currentReading?: string | null;
  /** 当前用量 */
  currentUsage?: string | null;
  /** 记录月份 */
  recordMonth?: string | null;
  /** 表计编号 */
  meterNo?: string;
  /** 余量方式 */
  surplusMethod?: string;
  /** 之前余额 */
  surplusBeforeAmount?: string;
  /** 之后余额 */
  surplusAfterAmount?: string;
}
